import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Grid,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure
} from '@chakra-ui/react';
import {
  SearchIcon,
  QuestionIcon,
  CloseIcon,
} from '@chakra-ui/icons';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { format } from 'date-fns';
import { supabase } from '../supabaseClient';

const MemberAuditTrail = ({ memberId, isModal = false }) => {
  const [auditTrail, setAuditTrail] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('changed_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [actionFilter, setActionFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [userEmails, setUserEmails] = useState({});
  const [contactNames, setContactNames] = useState({});
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    fetchAuditTrail();
  }, [memberId, timeFilter]);

  const getContactNameFromData = (data) => {
    if (!data) return '';
    const { first_name, last_name } = data;
    return `${first_name || ''} ${last_name || ''}`.trim();
  };

  const fetchContactName = async (contactId, action, oldData) => {
    // For deleted records, use old_data
    if (action === 'DELETE') {
      const name = getContactNameFromData(oldData);
      if (name) {
        setContactNames(prev => ({
          ...prev,
          [contactId]: name
        }));
        return name;
      }
      return '';
    }

    // For existing records, check cache first
    if (contactNames[contactId]) return contactNames[contactId];
    
    try {
      const { data, error } = await supabase
        .from('texting_contacts')
        .select('first_name, last_name')
        .match({ id: contactId });

      if (error) throw error;
      
      // Get the first matching contact
      const contact = data?.[0];
      const fullName = contact ? getContactNameFromData(contact) : '';
      
      setContactNames(prev => ({
        ...prev,
        [contactId]: fullName
      }));
      return fullName;
    } catch (error) {
      console.error('Error fetching contact name:', error);
      // Fallback to old_data if database query fails
      const name = getContactNameFromData(oldData);
      if (name) {
        setContactNames(prev => ({
          ...prev,
          [contactId]: name
        }));
        return name;
      }
      return '';
    }
  };

  const fetchUserEmail = async (userId) => {
    if (userEmails[userId]) return userEmails[userId];
    
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      if (currentUser && currentUser.id === userId) {
        setUserEmails(prev => ({
          ...prev,
          [userId]: currentUser.email
        }));
        return currentUser.email;
      }
      
      return userId.slice(0, 8) + '...';
    } catch (error) {
      console.error('Error checking user:', error);
      return userId.slice(0, 8) + '...';
    }
  };

  const fetchAuditTrail = async () => {
    try {
      let query = supabase
        .from('contact_audit_log')
        .select('*');

      if (memberId) {
        query = query.eq('contact_id', memberId);
      }

      if (timeFilter !== 'all') {
        const now = new Date();
        let startDate = new Date();
        
        switch (timeFilter) {
          case 'today':
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate.setMonth(now.getMonth() - 1);
            break;
        }
        
        query = query.gte('changed_at', startDate.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;
      setAuditTrail(data || []);

      // Fetch names for all contacts and users
      const uniqueUserIds = [...new Set(data.map(log => log.changed_by))];
      uniqueUserIds.forEach(fetchUserEmail);

      // Fetch contact names, passing action and old_data
      data.forEach(log => {
        fetchContactName(log.contact_id, log.action, log.old_data);
      });
    } catch (error) {
      console.error('Error fetching audit trail:', error);
    }
  };

  const formatChange = (entry) => {
    try {
      // For deleted records, use the name from old_data
      const getContactName = () => {
        if (entry.action === 'DELETE' && entry.old_data) {
          const { first_name, last_name } = entry.old_data;
          return `${first_name || ''} ${last_name || ''}`.trim();
        }
        return contactNames[entry.contact_id] || '';
      };

      const contactName = getContactName();
      const namePrefix = contactName ? ` for ${contactName}` : '';

      switch (entry.action) {
        case 'INSERT':
          return `Created new contact record${namePrefix}`;
        
        case 'DELETE':
          return `Deleted contact record${namePrefix}`;
        
        case 'UPDATE':
          // Define fields to ignore
          const ignoredFields = [
            'updated_at',
            'created_at',
            'id',
            'contact_id',
            'user_id'
          ];

          // Define fields that should show from/to values
          const showFromToFields = [
            'first_name',
            'last_name',
            'full_name',
            'phone_number',
            'email'
          ];

          const changes = Object.keys(entry.new_data || {}).map(key => {
            if (ignoredFields.includes(key)) return null;
            
            const oldValue = entry.old_data?.[key];
            const newValue = entry.new_data[key];
            
            if (oldValue !== newValue) {
              // Handle special cases
              if (key === 'latest_consent_id' || key === 'consent_verification_token') {
                return 'consent information';
              }
              
              // Show from/to for important fields
              if (showFromToFields.includes(key)) {
                const fieldName = key.replace(/_/g, ' ');
                return `${fieldName} from ${oldValue || 'empty'} to ${newValue}`;
              }
              
              // Handle notes separately
              if (key === 'notes') {
                return 'notes';
              }
              
              // Default case: just show the field name
              return key.replace(/_/g, ' ');
            }
            return null;
          }).filter(Boolean);

          // Remove duplicates and clean up
          const uniqueChanges = [...new Set(changes)];
          
          if (uniqueChanges.length === 0) return `No changes detected${namePrefix}`;
          if (uniqueChanges.length === 1) return `Updated ${uniqueChanges[0]}${namePrefix}`;
          if (uniqueChanges.length === 2) return `Updated ${uniqueChanges[0]} and ${uniqueChanges[1]}${namePrefix}`;
          return `Updated ${uniqueChanges.slice(0, -1).join(', ')} and ${uniqueChanges[uniqueChanges.length - 1]}${namePrefix}`;
        
        default:
          return 'Unknown change';
      }
    } catch (error) {
      console.error('Error formatting change:', error);
      return 'Error formatting change data';
    }
  };

  // Filter and sort audit trail
  const filteredAndSortedAuditTrail = useMemo(() => {
    let result = [...auditTrail];
    
    // Filter by search term
    if (searchQuery) {
      const lowercaseSearch = searchQuery.toLowerCase();
      result = result.filter(entry => {
        const changeText = formatChange(entry).toLowerCase();
        const userText = (userEmails[entry.changed_by] || '').toLowerCase();
        return changeText.includes(lowercaseSearch) || userText.includes(lowercaseSearch);
      });
    }

    // Filter by action type
    if (actionFilter !== 'all') {
      result = result.filter(entry => entry.action === actionFilter);
    }
    
    // Sort entries
    result.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      
      if (sortField === 'changed_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const comparison = aValue.toString().localeCompare(bValue.toString());
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }, [auditTrail, searchQuery, actionFilter, sortField, sortDirection, userEmails]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <Icon as={FaSort} />;
    return sortDirection === 'asc' ? <Icon as={FaSortUp} /> : <Icon as={FaSortDown} />;
  };

  return (
    <Box p={1}>
      {!isModal && (
        <Flex justify="space-between" align="center" mb={2}>
          <Heading size="sm">Contact Audit Trail</Heading>
          <IconButton
            icon={<QuestionIcon />}
            size="sm"
            variant="ghost"
            onClick={onOpen}
            aria-label="Help"
          />
        </Flex>
      )}

      <VStack spacing={1} align="stretch">
        <Grid templateColumns="1fr 120px 160px" gap={2}>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents='none'>
              <SearchIcon color='gray.300' />
            </InputLeftElement>
            <Input
              placeholder="Search changes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="sm"
            />
            {searchQuery && (
              <InputRightElement>
                <IconButton
                  icon={<CloseIcon />}
                  size="xs"
                  variant="ghost"
                  colorScheme="gray"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery('')}
                />
              </InputRightElement>
            )}
          </InputGroup>
          <Select
            size="sm"
            variant="outline"
            fontWeight="normal"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
          >
            <option value="all">All Actions</option>
            <option value="INSERT">INSERT</option>
            <option value="UPDATE">UPDATE</option>
            <option value="DELETE">DELETE</option>
          </Select>
          <Select
            size="sm"
            variant="outline"
            fontWeight="normal"
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </Select>
        </Grid>

        <Table variant="simple" size="xs">
          <Thead>
            <Tr>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('changed_at')}>
                Date {getSortIcon('changed_at')}
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('action')}>
                Action {getSortIcon('action')}
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal">Changes</Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('changed_by')}>
                Changed By {getSortIcon('changed_by')}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredAndSortedAuditTrail.map((entry) => (
              <Tr key={entry.id} fontSize="xs">
                <Td py={0} px={1}>{format(new Date(entry.changed_at), 'yyyy-MM-dd HH:mm')}</Td>
                <Td py={0} px={1}>
                  <Text
                    color={
                      entry.action === 'INSERT' ? 'green.500' :
                      entry.action === 'UPDATE' ? 'blue.500' :
                      entry.action === 'DELETE' ? 'red.500' : 'gray.500'
                    }
                  >
                    {entry.action}
                  </Text>
                </Td>
                <Td py={0} px={1}>
                  <Box 
                    overflowX="auto" 
                    whiteSpace="nowrap" 
                    maxWidth="400px"
                    sx={{
                      '&::-webkit-scrollbar': {
                        height: '3px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'gray.200',
                        borderRadius: '1px',
                      }
                    }}
                  >
                    <Text>{formatChange(entry)}</Text>
                  </Box>
                </Td>
                <Td py={0} px={1}>{userEmails[entry.changed_by] || entry.changed_by}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </VStack>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>Contact Audit Trail Help</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto">
            <VStack align="stretch" spacing={4}>
              <Box>
                <Heading size="sm" mb={2}>Overview</Heading>
                <Text pl={4}>
                  The Contact Audit Trail shows all changes made to contact information.
                  Each row represents a single change event, including insertions, updates, and deletions.
                </Text>
              </Box>
              <Box>
                <Heading size="sm" mb={2}>Columns</Heading>
                <VStack align="stretch" spacing={1} pl={4}>
                  <Text>• Date: When the change was made (yyyy-MM-dd HH:mm)</Text>
                  <Text>• Action: Type of change (INSERT, UPDATE, or DELETE)</Text>
                  <Text>• Changes: Details of what was changed (scroll horizontally if content is longer than the column)</Text>
                  <Text>• Changed By: Email of the user who made the change</Text>
                </VStack>
              </Box>
              <Box>
                <Heading size="sm" mb={2}>Features</Heading>
                <VStack align="stretch" spacing={1} pl={4}>
                  <Text>• Search: Filter changes by searching the changes or user email</Text>
                  <Text>• Sort: Click any column header to sort by that field. Click again to reverse the sort order</Text>
                  <Text>• Action Filter: Filter changes by type (All, INSERT, UPDATE, DELETE)</Text>
                  <Text>• Time Filter: Filter changes by time period:
                    <VStack align="stretch" spacing={0} pl={4} mt={1}>
                      <Text>- All Time: Show all changes ever made (default)</Text>
                      <Text>- Today: Show only changes made today</Text>
                      <Text>- Last 7 Days: Show changes from the past week</Text>
                      <Text>- Last 30 Days: Show changes from the past month</Text>
                    </VStack>
                  </Text>
                </VStack>
              </Box>
              <Box>
                <Heading size="sm" mb={2}>Color Coding</Heading>
                <VStack align="stretch" spacing={1} pl={4}>
                  <Text>• Green: Contact inserted (INSERT)</Text>
                  <Text>• Blue: Contact updated (UPDATE)</Text>
                  <Text>• Red: Contact deleted (DELETE)</Text>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MemberAuditTrail;
